import Events from 'events'
import { PLAYER_TYPE, DEFAULT_MEDIA_BLOCK_CONFIG } from '../config'
import { getLogger } from '../util/log'
import * as CombineMode from './CombineMode'
import createVolumeMeter from './function/createVolumeMeter'
import PacketLoss from '../util/PacketLoss'
import { getPlayerIdFromSessionId, generateSessionId, getStreamType, getUserId } from '../util/util'
import MediaBlockManager from '../util/MediaBlockManager'

const logger = getLogger('TRTC')

export default class TRTCClient extends Events {
    constructor({ mode = 'live', codec = 'h264', appId, token, channel, userId, streamType, webrtcInfo, lags }) {
        super()

        this.mode = mode
        this.codec = codec
        this.appId = appId
        this.token = token
        this.channel = channel
        this.userId = userId
        this.streamType = streamType
        this.webrtcInfo = webrtcInfo
        this.lags = Object.assign(DEFAULT_MEDIA_BLOCK_CONFIG, lags)

        this.isJoined = false
        this.isPublished = false
        this.isUnpublishIng = false
        this.locationStream = null
        this.subscribeQueue = {}
        this.session = '' + generateSessionId(userId, streamType)

        this.canSubscribe = streamType === PLAYER_TYPE.MAIN_CAMERA

        this.mediaBlockManager = new MediaBlockManager(this.lags)

        this.upState = {
            bytesSentVideo: 0,
            bytesSentAudio: 0,
            uplinkVideoLossRate: 0.0,
            uplinkAudioLossRate: 0.0,
            uplinkVideoBandwidth: 0,
            uplinkAudioBandwidth: 0,
            uplinkFrameRate: 0,
            rtt: 0,
            mediaBlock: this.mediaBlockManager.createMediaBlockInstance(this.session, true, false, this.lags)
        }
        this.downState = {}

        CombineMode.addClient(this.streamType, this)

        this.client = global.TRTC.createClient({
            mode: this.mode,
            sdkAppId: this.appId,
            userId: this.session,
            userSig: this.token,
            useStringRoomId: true,
            // autoSubscribe: this.canSubscribe
            autoSubscribe: false
        })

        if (!this.canSubscribe && this.client.setDefaultMuteRemoteStreams) {
            this.client.setDefaultMuteRemoteStreams(true)
        }

        this.handleEvents()

        this.streamAddHold = true
    }

    async join() {
        if (this.isJoined) {
            logger.error('duplicate RtcClient.join() observed')
            throw new Error('join fail')
        }
        try {
            // join the room
            if (this.canSubscribe) {
                await this.client.join({
                    roomId: this.channel
                })
                logger.info('join room success')
                this.isJoined = true
            }
            this.streamAddHold = false
            CombineMode.publisherCache(this.streamType)
        } catch (error) {
            logger.error('failed to join room because: ' + error)
            throw new Error('join fail')
        }
    }

    async leave() {
        if (!this.isJoined) {
            logger.error('RtcClient.join() not observed')
            throw new Error('leave fail')
        }
        try {
            if (this.isPublished && this.locationStream) {
                await this.unpublish(this.locationStream)
            }
            await this.client.leave()
            logger.info('leave room success')
            this.isJoined = false
            this.streamAddHold = true
            this.emit('quit')
        } catch (error) {
            this.isJoined = false
            this.streamAddHold = true
            logger.error('failed to leave room because: ' + error)
            throw new Error('leave fail')
        }
    }

    async publish(stream) {
        if (!this.isJoined) {
            if (this.canSubscribe) {
                logger.error('publish() - please join() firstly')
                throw new Error('publish fail')
            } else {
                // 非主 session 推流的时候才 join
                await this.client.join({
                    roomId: this.channel
                })
                logger.info('join room success')
                this.isJoined = true
            }
        }
        if (this.isPublished) {
            logger.info('duplicate RtcClient.publish() observed')
            throw new Error('publish fail')
        }
        if (this.publishTimeout) {
            clearTimeout(this.publishTimeout)
            this.publishTimeout = null
        }
        try {
            // 发布本地流
            this.publishTimeout = setTimeout(() => {
                this.publishTimeout = null
                throw new Error('publish fail')
            }, 10 * 1000)

            await this.client.publish(stream)

            clearTimeout(this.publishTimeout)
            this.publishTimeout = null

            this.isPublished = true
            this.locationStream = stream
        } catch (error) {
            if (this.publishTimeout) {
                clearTimeout(this.publishTimeout)
                this.publishTimeout = null
            }
            logger.error('failed to publish local stream ' + error)
            this.isPublished = false
            throw new Error('publish fail')
        }
    }

    async unpublish(stream) {
        if (!this.isJoined) {
            logger.error('unpublish() - please join() firstly')
            throw new Error('unpublish fail')
        }
        if (!this.isPublished && !this.isUnpublishIng) {
            logger.info('RtcClient.unpublish() called but not published yet')
            return
        }

        if (this.publishTimeout) {
            clearTimeout(this.publishTimeout)
            this.publishTimeout = null
        }

        try {
            // 停止发布本地流
            this.isUnpublishIng = true
            await this.client.unpublish(this.locationStream || stream)
            this.upState.userId = null
            this.isPublished = false
            this.isUnpublishIng = false
            this.locationStream = null

            // 非主 session 结束推流时 leave room
            if (!this.canSubscribe) {
                await this.client.leave()
                logger.info('leave room success')
                this.isJoined = false
            }
        } catch (error) {
            this.isUnpublishIng = false
            this.isPublished = false
            logger.error('failed to unpublish local stream because ' + error)
            if (!/stream has not been published yet/i.test(error)) {
                throw new Error('unpublish fail')
            }
        }
    }

    async _subscribe(stream) {
        if (!this.isJoined) {
            logger.error('subscribe() - please join() firstly')
            throw new Error('subscribe fail')
        }
        try {
            await this.client.subscribe(stream, { video: true, audio: true })
            let sessionId = stream.getUserId()
            let isPush = false
            let hasLossRate = true

            this.downState[sessionId] = {
                bytesReceivedVideo: 0,
                bytesReceivedAudio: 0,
                audioPacketLoss: new PacketLoss(),
                videoPacketLoss: new PacketLoss(),
                downlinkVideoBandwidth: 0,
                downlinkAudioBandwidth: 0,
                mediaBlock: this.mediaBlockManager.createMediaBlockInstance(sessionId, isPush, hasLossRate)
            }
            return {
                userId: getPlayerIdFromSessionId(sessionId),
                stream: stream
            }
        } catch (error) {
            logger.error('failed to subscribe remote stream because ' + error)
            throw new Error('subscribe fail')
        }
    }

    async subscribe(stream) {
        try {
            return await CombineMode.subscribe(stream)
        } catch (event) {
            throw new Error('subscribe fail')
        }
    }

    async _mutePeer(stream, media) {
        if (!this.isJoined) {
            logger.error('subscribe() - please join() firstly')
            throw new Error('subscribe fail')
        }
        if (!stream) {
            throw new Error('subscribe fail - stream is null')
        }
        try {
            logger.info('mutePeer', media)
            try {
                await this.client.unsubscribe(stream)
            } catch (error) {
                logger.error('_mutePeer: failed to unsubscribe remote stream because ' + error)
            }

            stream && (await this.client.subscribe(stream, media))

            if (!media.video && stream && this.downState[stream.getUserId()]) {
                this.downState[stream.getUserId()].downlinkVideoBandwidth = 0
            }
        } catch (error) {
            logger.error('failed to subscribe remote stream because ' + error)
            throw new Error('subscribe fail')
        }
    }

    async mutePeer(stream, media) {
        try {
            await CombineMode.mutePeer(stream, media)
        } catch (event) {
            throw new Error('subscribe fail')
        }
    }

    async _unsubscribe(stream) {
        if (!this.isJoined) {
            logger.error('unsubscribe() - please join() firstly')
            throw new Error('unsubscribe fail')
        }
        try {
            await this.client.unsubscribe(stream)
            let sessionId = stream.getUserId()
            if (this.downState[sessionId]) {
                delete this.downState[sessionId]
            }
        } catch (error) {
            logger.info('failed to unsubscribe remote stream because ' + error)
            throw new Error('unsubscribe fail')
        }
    }

    async unsubscribe(stream) {
        try {
            await CombineMode.unsubscribe(stream)
        } catch (event) {
            throw new Error('subscribe fail')
        }
    }

    handleEvents() {
        // 处理 client 错误事件，错误均为不可恢复错误，建议提示用户后刷新页面
        this.client.on('error', async (error) => {
            logger.error(error)
            if (error && error.getCode && error.getCode() == 70001) {
                // 签名过期直接销毁
                await this.destroy()
            } else if (!/DTLS Transport connection timeout/i.test(error)) {
                await CombineMode.quit()
            } else {
                await this.destroy()
            }
        })

        if (this.canSubscribe) {
            // 处理远端流增加事件
            this.client.on('stream-added', (evt) => {
                // 20220722 trtc 进房间会先收到该事件 join 异步才成功 导致第一次订阅失败 这里更新下状态兼容下 预计 0729 修复
                if (!this.isJoined) {
                    this.isJoined = true
                }
                const remoteStream = evt.stream
                const id = remoteStream.getId()
                const sessionId = remoteStream.getUserId()
                remoteStream.stream = remoteStream.mediaStream_

                if (this.userId === getUserId(sessionId)) {
                    this.client.unsubscribe(remoteStream)
                    remoteStream.close()
                } else {
                    createVolumeMeter(remoteStream)
                    logger.info(`remote stream added: [${sessionId}] ID: ${id} type: ${remoteStream.getType()}`)
                    CombineMode.addPublisher(sessionId, remoteStream)
                }
            })

            // 处理远端流被删除事件
            this.client.on('stream-removed', (evt) => {
                const remoteStream = evt.stream
                const id = remoteStream.getId()
                const sessionId = remoteStream.getUserId()
                if (this.userId !== getUserId(sessionId)) {
                    remoteStream.close()
                    logger.info(`stream-removed ID: ${id}  type: ${remoteStream.getType()}`)
                    CombineMode.removePublish(sessionId, remoteStream)
                }
                if (this.downState[sessionId]) {
                    delete this.downState[sessionId]
                }
            })

            this.client.on('stream-updated', (evt) => {
                const remoteStream = evt.stream
                remoteStream.stream = remoteStream.mediaStream_
                const id = remoteStream.getId()
                const sessionId = remoteStream.getUserId()
                if (this.userId !== getUserId(sessionId)) {
                    createVolumeMeter(remoteStream)
                    logger.info(`stream-update ID: ${id}  type: ${remoteStream.getType()}`)
                    CombineMode.updatePublish(sessionId, remoteStream)
                }
            })
        }

        this.stateTimer = setInterval(() => {
            if (this.isPublished) {
                this.client.getLocalAudioStats().then((stats) => {
                    for (let sessionId in stats) {
                        this.upState.uplinkAudioBandwidth = stats[sessionId].bytesSent - this.upState.bytesSentAudio
                        this.upState.bytesSentAudio = stats[sessionId].bytesSent
                    }
                })
                this.client.getLocalVideoStats().then((stats) => {
                    for (let sessionId in stats) {
                        this.upState.uplinkVideoBandwidth = stats[sessionId].bytesSent - this.upState.bytesSentVideo
                        this.upState.bytesSentVideo = stats[sessionId].bytesSent
                        this.upState.frameRate = stats[sessionId].framesSent - this.upState.framesSent
                        this.upState.framesSent = stats[sessionId].framesSent
                    }
                })
                this.client.getTransportStats().then((stats) => {
                    this.upState.rtt = stats.rtt
                })
            }

            if (this.canSubscribe) {
                if (Object.keys(this.downState).length) {
                    this.client.getRemoteAudioStats().then((stats) => {
                        for (let sessionId in stats) {
                            if (this.downState[sessionId]) {
                                this.downState[sessionId].downlinkAudioBandwidth =
                                    stats[sessionId].bytesReceived - this.downState[sessionId].bytesReceivedAudio
                                this.downState[sessionId].bytesReceivedAudio = stats[sessionId].bytesReceived
                                this.downState[sessionId].audioPacketLoss.addData(
                                    stats[sessionId].packetsReceived,
                                    stats[sessionId].packetsLost
                                )
                            }
                        }
                    })
                    this.client.getRemoteVideoStats().then((stats) => {
                        for (let sessionId in stats) {
                            if (this.downState[sessionId]) {
                                this.downState[sessionId].downlinkVideoBandwidth =
                                    stats[sessionId].bytesReceived - this.downState[sessionId].bytesReceivedVideo
                                this.downState[sessionId].bytesReceivedVideo = stats[sessionId].bytesReceived
                                this.downState[sessionId].videoPacketLoss.addData(
                                    stats[sessionId].packetsReceived,
                                    stats[sessionId].packetsLost
                                )
                                this.downState[sessionId].frameRate =
                                    stats[sessionId].framesDecoded -
                                    (this.downState[sessionId].framesDecoded
                                        ? this.downState[sessionId].framesDecoded
                                        : 0)
                                this.downState[sessionId].framesDecoded = stats[sessionId].framesDecoded
                            }
                        }
                    })
                }
            }
            if (this.isPublished) {
                this.emit('rtcat-stats', {
                    isPublish: true,
                    log: this.upState
                })
            }

            if (this.canSubscribe) {
                for (let sessionId in this.downState) {
                    CombineMode.emitDownloadState(sessionId, {
                        downlinkAudioBandwidth: this.downState[sessionId].downlinkAudioBandwidth,
                        downlinkVideoBandwidth: this.downState[sessionId].downlinkVideoBandwidth,
                        downlinkAudioLossRate: this.downState[sessionId].audioPacketLoss.getPacketLossRate(),
                        downlinkVideoLossRate: this.downState[sessionId].videoPacketLoss.getPacketLossRate()
                    })
                }
            }
        }, 1000)

        this.blockTimer = setInterval(() => {
            if (this.isPublished) {
                let state = this.upState
                let mediaBlock = state.mediaBlock
                let _state = {
                    fps: state.frameRate,
                    audioBandwidth: state.uplinkAudioBandwidth,
                    videoBandwidth: state.uplinkVideoBandwidth
                }
                let isBlock = mediaBlock.isBlock(_state)
                window.enableBJYDebugLog &&
                    logger.debug(
                        'tcent-uplink-state:',
                        this.session,
                        ', current:',
                        _state,
                        ', winAvg:',
                        mediaBlock.getValues()
                    )
                if (isBlock) {
                    window.enableBJYDebugLog &&
                        logger.debug('tcent-uplink-flency-report', this.session, mediaBlock.getValues())
                    CombineMode.flencyReport(this.session)
                }
            }
            if (this.canSubscribe) {
                for (let sessionId in this.downState) {
                    let state = this.downState[sessionId]
                    let mediaBlock = state.mediaBlock
                    let _state = {
                        audioLossRate: state.audioPacketLoss.getPacketLossRate(),
                        videoLossRate: state.videoPacketLoss.getPacketLossRate(),
                        fps: state.frameRate,
                        audioBandwidth: state.downlinkAudioBandwidth,
                        videoBandwidth: state.downlinkVideoBandwidth
                    }
                    let isBlock = mediaBlock.isBlock(_state)
                    window.enableBJYDebugLog &&
                        logger.debug(
                            'tcent-downlink-state:',
                            sessionId,
                            ', current:',
                            _state,
                            ', winAvg:',
                            mediaBlock.getValues()
                        )
                    if (isBlock) {
                        window.enableBJYDebugLog &&
                            logger.debug('tcent-downlink-flency-report', sessionId, mediaBlock.getValues())
                        CombineMode.flencyReport(sessionId)
                    }
                }
            }
        }, 2000)
    }

    async destroy() {
        if (this.isJoined) {
            try {
                await this.client.leave()
                this.isJoined = false
            } catch (error) {
                logger.error('leave room error', error)
            }
        }
        this.emit('quit')
        this.removeAllListeners()
        CombineMode.removeClient(this.streamType)
        this.client = null
    }

    removeAllListeners() {
        clearInterval(this.stateTimer)
        this.stateTimer = null
        clearInterval(this.blockTimer)
        this.blockTimer = null
        if (this.client) {
            this.client.off('*')
        }
    }
}
